import { Reason } from '../admin/Reason.js';
// The Billing tab of the admin console (milestone 8 check 6; module 12 A22, DESIGN §7; module 10
// DESIGN "The billing map on the admin console"). The Snowflake tab's own screen for a third key:
// which Anaplan billing customer each company is. Proposed from finance's Operator value,
// confirmed by a person, rejections kept. The evidence beside each row is the customer's latest
// contract end and what it was billed, because two names are not enough to confirm a client by.
// Its own fetch helpers and its own small copies of the console's load hook, so this file shares
// no code path with AdminConsole.tsx beyond the three lines that mount it.
import { ActionButton } from '../parts/index.js';
import { viewFetch as fetch } from '../view-as.js';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLiveRefresh } from '../live.js';
import { ApiError, Unauthenticated, type Store } from '../api.js';
import '../admin/admin.css';
import './billing-map.css';

import { applyBillingDecision, carriesDecision, linkedByCobrain, type BillingDecided, type BillingMapRow, type BillingMapView } from './decide.js';
export type { BillingMapRow } from './decide.js';

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json().then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null)).catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}
const api = {
  list: (tenantId: string) => fetch(`/api/admin/billing?store=${encodeURIComponent(tenantId)}`, { credentials: 'same-origin' }).then((r) => json<BillingMapView>(r)),
  decide: (tenantId: string, id: string, verb: 'confirm' | 'reject', reason: string) =>
    fetch(`/api/admin/billing/${encodeURIComponent(id)}/${verb}`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store: tenantId, reason }) }).then((r) => json<{ decided?: Partial<BillingDecided> }>(r)),
};

function said(e: unknown): string {
  if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return 'signing you in again…'; }
  return e instanceof Error ? e.message : String(e);
}

const dash: ReactNode = <span className="dim">—</span>;
const pounds = (v: number | null): ReactNode => (v === null ? dash : `£${Math.round(v).toLocaleString('en-GB')}`);

function matchedBy(r: BillingMapRow): ReactNode {
  if (!r.matchedOn) return <span className="dim">added by hand</span>;
  return <>{r.matchedOn === 'operator' ? 'Operator' : 'customer name'} · {r.basis ?? '—'}{r.proposedName && r.proposedName !== r.customer && r.proposedName !== r.operator ? <div className="dim small">“{r.proposedName}”</div> : null}</>;
}

function stateCell(r: BillingMapRow): ReactNode {
  // A link Cobrain made itself says so, with its reason: it is the one a person may undo.
  if (linkedByCobrain(r)) return <>linked by Cobrain{r.cobrainLinkedAt ? <><br /><span className="dim">{r.cobrainLinkedAt.slice(0, 10)}</span></> : null}{r.cobrainReason ? <div className="dim small">{r.cobrainReason}</div> : null}</>;
  if (r.state === 'confirmed') return <>confirmed by {r.confirmedBy ?? 'a person'}{r.confirmedAt ? <><br /><span className="dim">{r.confirmedAt.slice(0, 10)}</span></> : null}</>;
  if (r.state === 'rejected') return <>rejected{r.rejectedAt ? <><br /><span className="dim">{r.rejectedAt.slice(0, 10)}</span></> : null}</>;
  return <span className="dim">proposed</span>;
}

export function BillingMap({ store }: { store: Store }) {
  const [data, setData] = useState<BillingMapView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [reason, setReason] = useState('');
  const canSave = reason.trim().length > 0;
  const [busy, setBusy] = useState<string | null>(null);
  const reload = useCallback(() => setTick((x) => x + 1), []);
  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.list(store.tenantId).then((d) => { if (!cancelled) setData(d); }).catch((e: unknown) => { if (!cancelled) setError(said(e)); });
    return () => { cancelled = true; };
  }, [store.tenantId, tick]);

  // Read again when he comes back to the tab (B1268, live.ts): the nightly copy from Anaplan
  // lands while nobody is looking, and the unmatched list is the first thing it changes.
  useLiveRefresh(reload);
  const act = (id: string, verb: 'confirm' | 'reject') => {
    if (!canSave || busy !== null) return;
    setBusy(id); setError(null);
    // The answer says what was written on the row, so the row is changed where it is held and the list is not read
    // again (B395's pattern): that read is twelve months of the worksheet for every row, once a press, for about 150
    // presses. An answer without the decision (an older API) falls back to reading the list.
    api.decide(store.tenantId, id, verb, reason.trim())
      .then((res) => {
        setReason(''); const d = res.decided; if (carriesDecision(d)) setData((held) => (held ? applyBillingDecision(held, d) : held)); else reload(); })
      .catch((e: unknown) => setError(said(e)))
      .finally(() => setBusy(null));
  };
  return (
    <>
      {error ? <p className="admin-error" role="alert">{error}</p> : null}
      <div className="admin-head"><h2>Billing map</h2><span>which Anaplan billing customer each company is</span></div>
      <Reason value={reason} onChange={setReason} busy={busy !== null} />
      <p className="admin-note">
        Proposed from the Operator finance records against each customer in Anaplan, and from the customer&rsquo;s own name where there is
        none. Where that name is exactly a company&rsquo;s name, Cobrain links the two itself and says so on the row; Undo puts it back as a
        rejection. A match on an alias or a trimmed name waits here for a person. One company may be billed under several names: each is
        its own row. A rejection is kept, so the same proposal is not made again. Anaplan&rsquo;s own total and the group&rsquo;s internal-revenue rows are never proposed.
      </p>
      <p className="admin-note">
        Undecided first, then largest by what the customer was billed over the last twelve months with actuals, so the clients carrying
        the revenue are the first rows and the tail is optional. Contract ends are read at Anaplan&rsquo;s Forecast version. A blank means the copy
        holds nothing for that customer, which is worth seeing before you confirm it.
        <strong> No billing figure or contract date from Anaplan appears on a company&rsquo;s page until its row here is linked or confirmed.</strong>
      </p>
      {data === null && !error ? <p className="meta pad">Reading the map…</p> : null}
      {data ? (
        <>
          <div className="admin-head"><h2>Customers</h2><span>{data.counts.linked.toLocaleString('en-GB')} companies linked · {data.counts.candidates.toLocaleString('en-GB')} proposed and waiting for a person · {data.counts.rejected.toLocaleString('en-GB')} rejected · {data.counts.customers.toLocaleString('en-GB')} rows</span></div>
          <div className="admin-scroll">
            <table className="admin billing-map">
              <thead><tr><th>Company</th><th>Anaplan customer</th><th>Matched by</th><th>Contract end</th><th className="num">Billed, 12 mo</th><th>State</th><th className="billing-actions" /></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className={r.state === 'candidate' ? undefined : 'decided'} data-billing-customer={r.customer}>
                    <td><strong>{r.organisationName}</strong></td>
                    <td>{r.customer}<div className="dim small">{r.operator ? `finance’s Operator: ${r.operator}` : 'no Operator in Anaplan'}</div></td>
                    <td>{matchedBy(r)}</td>
                    <td>{r.latestContractEnd ?? dash}{r.monthsTillContractEnd !== null ? <div className="dim small">{r.monthsTillContractEnd <= 0 ? 'ended, by finance’s count' : `${Math.round(r.monthsTillContractEnd)} months, by finance’s count`}</div> : null}</td>
                    <td className="num">{pounds(r.billedLast12)}</td>
                    <td>{stateCell(r)}</td>
                    <td className="billing-actions">{r.state === 'candidate' ? (
                      <div className="admin-actions">
                        <ActionButton className="small" type="button" disabled={busy !== null || !canSave} onClick={() => act(r.id, 'confirm')}>Confirm</ActionButton>
                        <ActionButton className="small danger" type="button" disabled={busy !== null || !canSave} onClick={() => act(r.id, 'reject')}>Reject</ActionButton>
                      </div>
                    ) : linkedByCobrain(r) ? (
                      <div className="admin-actions">
                        <ActionButton className="small danger" type="button" disabled={busy !== null || !canSave} onClick={() => act(r.id, 'reject')} title="Undo Cobrain's link: the row becomes a rejection, and Cobrain does not link this company to this customer again">Undo</ActionButton>
                      </div>
                    ) : null}</td>
                  </tr>
                ))}
                {data.rows.length === 0 ? <tr><td colSpan={7} className="dim">Nothing proposed yet: the proposal runs after the nightly copy of Anaplan has landed on this store.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </>
  );
}
